import type React from "react";
import {
  Fragment,
  type ReactNode,
  useCallback,
  useEffect,
  useState,
} from "react";
import { cn } from "../lib/utils";
import { Checkbox } from "./checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "./tooltip";

export interface TreeNode {
  id: string;
  label: string;
  defaultChecked?: boolean;
  children?: TreeNode[];
  className?: string;
  disabled?: boolean;
  tooltip?: string;
}

// @todo: should add ref or else to manually reset checknodes state
function useCheckboxTree(initialTree: TreeNode) {
  const [checkedNodes, setCheckedNodes] = useState<Set<string>>(
    new Set<string>(),
  );

  useEffect(() => {
    const initialCheckedNodes = new Set<string>();

    const initializeCheckedNodes = (node: TreeNode) => {
      if (node.defaultChecked) {
        initialCheckedNodes.add(node.id);
      }
      node.children?.forEach(initializeCheckedNodes);
    };
    initializeCheckedNodes(initialTree);

    setCheckedNodes(initialCheckedNodes);
  }, [initialTree]);

  const isChecked = useCallback(
    (node: TreeNode): boolean | "indeterminate" => {
      if (!node.children) {
        return checkedNodes.has(node.id);
      }

      const childrenChecked = node.children.map((child) => isChecked(child));
      if (childrenChecked.every((status) => status === true)) {
        return true;
      }
      if (
        childrenChecked.some(
          (status) => status === true || status === "indeterminate",
        )
      ) {
        return "indeterminate";
      }
      return false;
    },
    [checkedNodes],
  );

  const handleCheck = useCallback(
    (node: TreeNode) => {
      const newCheckedNodes = new Set(checkedNodes);

      const toggleNode = (n: TreeNode, check: boolean) => {
        if (check) {
          newCheckedNodes.add(n.id);
        } else {
          newCheckedNodes.delete(n.id);
        }
        // biome-ignore lint/complexity/noForEach: <forEach is appropriate for recursive tree traversal with Set modifications>
        n.children?.forEach((child) => toggleNode(child, check));
      };

      const currentStatus = isChecked(node);
      const newCheck = currentStatus !== true;

      toggleNode(node, newCheck);
      setCheckedNodes(newCheckedNodes);

      return Array.from(newCheckedNodes.values());
    },
    [checkedNodes, isChecked],
  );

  return { isChecked, handleCheck };
}

const defaultRenderNode = ({
  node,
  isChecked,
  onCheckedChange,
  children,
}: {
  node: TreeNode;
  isChecked: boolean | "indeterminate";
  onCheckedChange: () => void;
  children: ReactNode;
}) => {
  const rowTpl = (
    <div className="flex items-center gap-2 py-1 hover:bg-gray-50 px-2 -mx-2">
      <Checkbox
        id={node.id}
        checked={isChecked}
        disabled={node.disabled}
        onCheckedChange={onCheckedChange}
      />

      <label
        htmlFor={node.id}
        className={cn(
          "text-sm text-gray-700 select-none cursor-pointer",
          node.className,
          node.disabled ? "text-gray-400" : "",
        )}
      >
        {node.label}
      </label>
    </div>
  );

  return (
    <Fragment key={node.id}>
      {node.tooltip ? (
        <Tooltip key={node.id}>
          <TooltipTrigger asChild>{rowTpl}</TooltipTrigger>
          <TooltipContent className="z-[9999]">{node.tooltip}</TooltipContent>
        </Tooltip>
      ) : (
        rowTpl
      )}

      {children && <>{children}</>}
    </Fragment>
  );
};

interface CheckboxTreeProps {
  tree: TreeNode;
  onCheckedChange?: ({
    node,
    isChecked,
    checkedNodeIds,
  }: { node: TreeNode; isChecked: boolean; checkedNodeIds: string[] }) => void;
  renderNode?: (props: {
    node: TreeNode;
    isChecked: boolean | "indeterminate";
    onCheckedChange: () => void;
    children: React.ReactNode;
  }) => React.ReactNode;
}

export function CheckboxTree({
  tree,
  onCheckedChange,
  renderNode = defaultRenderNode,
}: CheckboxTreeProps) {
  const { isChecked, handleCheck } = useCheckboxTree(tree);

  const renderTreeNode = (node: TreeNode): React.ReactNode => {
    const children = node.children?.map(renderTreeNode);
    const checked = isChecked(node);

    return renderNode({
      node,
      isChecked: checked,
      onCheckedChange: () => {
        const checkedNodeIds = handleCheck(node);
        const isChecked = checked === "indeterminate" || !checked;

        onCheckedChange?.({ node, isChecked, checkedNodeIds });
      },
      children,
    });
  };

  return renderTreeNode(tree);
}
